<script setup lang="ts">
  import SnippetIcon from '@/components/icons/snippet.vue'
  import { Dialog, DialogContent } from '@/components/ui/dialog'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { useTranslation } from '@/composables/useTranslation'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { useGlobalChatStore } from '@/store/app/globalChat/globalChatStore'
  import {
    AiChatSessionsService,
    AIChatSessionTypeEnum,
    SavedResponsesService,
    UsersService,
    type AIChatSession,
    type CustomerCase
  } from '@/types/generated'
  import {
    Atom,
    FileText,
    Info,
    ListRestart,
    Loader2,
    Plus,
    Scale,
    SendHorizontal,
    Upload,
    Users,
    X
  } from 'lucide-vue-next'
  import { computed, inject, nextTick, onMounted, provide, ref, watch, type Ref } from 'vue'
  import DocumentProcessingStatus from '../../cases/documents/DocumentProcessingStatus.vue'
  import AiChatVerdictFeatureHint from './AiChatVerdictFeatureHint.vue'
  import AiChatDocumentsPanel from './AiChatDocumentsPanel.vue'
  import AiChatParticipantsPanel from './AiChatParticipantsPanel.vue'
  import AiChatSnippetsPanel from './AiChatSnippetsPanel.vue'
  import EditSavedResponseDialog from './EditSavedResponseDialog.vue'
  import PromptTemplatesDialog from './PromptTemplatesDialog.vue'

  const { t } = useTranslation({
    en: {
      YourMessage: 'Your message...',
      Generating: 'Generating...',
      SendMessage: 'Send message',
      PromptTemplates: 'Prompt templates',
      UploadDocuments: 'Upload documents',
      EnhancePrompt: 'Enhance Prompt',
      Enhancing: 'Enhancing',
      CourtDatabase: 'Court database',
      ChatDocuments: 'Chat documents',
      Snippets: 'Snippets',
      Participants: 'Parties'
    },
    de: {
      YourMessage: 'Ihre Nachricht...',
      Generating: 'Generiere...',
      SendMessage: 'Nachricht senden',
      PromptTemplates: 'Promptvorlagen',
      UploadDocuments: 'Dokumente hochladen',
      EnhancePrompt: 'Prompt verbessern',
      Enhancing: 'Verbessert',
      CourtDatabase: 'Urteilsdatenbank',
      ChatDocuments: 'Chat-Dokumente',
      Snippets: 'Snippets',
      Participants: 'Beteiligte'
    }
  })

  // Inject case data and chat functionality
  const caseData = inject<Ref<CustomerCase | null>>('caseData', ref(null))
  const sessionType = inject<AIChatSessionTypeEnum>('sessionType', AIChatSessionTypeEnum.CASE)
  const sessionGroupData = inject<Ref<any>>('sessionGroupData', ref(null))
  const sendChatMessage = inject(
    'sendChatMessage',
    async (_session: AIChatSession | null, _message: string) => {}
  )
  const isSessionGenerating = inject<(uuid: string) => boolean>('isSessionGenerating', () => false)
  const activeChatSession = inject<Ref<AIChatSession | null>>('activeChatSession', ref(null))
  const isActiveSessionGenerating = computed(() => {
    return activeChatSession.value && isSessionGenerating(activeChatSession.value.uuid)
  })
  const userChatMessage = ref('')
  const textareaRef = ref<HTMLTextAreaElement | null>(null)
  const courtDatabaseToggleRef = ref<HTMLButtonElement | null>(null)

  // Reference to the status component for potential programmatic control
  const documentStatusRef = ref<InstanceType<typeof DocumentProcessingStatus> | null>(null)

  // Get stores for document processing data
  const caseStore = useCaseStore()
  const globalChatStore = useGlobalChatStore()
  const userStore = useUserStore()

  // Get the appropriate document processing data based on session type
  const documentProcessingData = computed(() => {
    if (sessionType === AIChatSessionTypeEnum.CASE) {
      return {
        documents: caseStore.documentProcessor.documents || [],
        processingDocuments: caseStore.documentProcessor.processingDocuments || [],
        activeUploads: caseStore.documentProcessor.activeUploads || [],
        documentsWithErrors: caseStore.documentProcessor.documentsWithErrors || []
      }
    } else {
      // For global chat, combine both processors
      const chatDocs = globalChatStore.chatSessionProcessor?.documents || []
      const folderDocs = globalChatStore.folderProcessor?.documents || []
      const chatProcessing = globalChatStore.chatSessionProcessor?.processingDocuments || []
      const folderProcessing = globalChatStore.folderProcessor?.processingDocuments || []
      const chatUploads = globalChatStore.chatSessionProcessor?.activeUploads || []
      const folderUploads = globalChatStore.folderProcessor?.activeUploads || []
      const chatErrors = globalChatStore.chatSessionProcessor?.documentsWithErrors || []
      const folderErrors = globalChatStore.folderProcessor?.documentsWithErrors || []

      return {
        documents: [...chatDocs, ...folderDocs],
        processingDocuments: [...chatProcessing, ...folderProcessing],
        activeUploads: [...chatUploads, ...folderUploads],
        documentsWithErrors: [...chatErrors, ...folderErrors]
      }
    }
  })

  // ---------------
  // Chat functionality
  // ---------------

  const sendMessage = () => {
    if (userChatMessage.value.trim() && !isActiveSessionGenerating.value) {
      sendChatMessage(activeChatSession.value, userChatMessage.value.trim())
      userChatMessage.value = ''
      // Reset textarea height after sending
      nextTick(() => {
        adjustTextareaHeight()
      })
    }
  }

  // ---------------
  // Prompt Enhancement functionality
  // ---------------

  const isEnhancingPrompt = ref(false)
  const enhancementError = ref<string | null>(null)
  const showEnhanceTooltip = ref(false)

  const enhancePrompt = async () => {
    if (
      !userChatMessage.value.trim() ||
      isEnhancingPrompt.value ||
      isActiveSessionGenerating.value
    ) {
      return
    }

    isEnhancingPrompt.value = true
    enhancementError.value = null

    try {
      const response = await AiChatSessionsService.aiChatSessionsEnhancePromptCreate({
        requestBody: {
          content: userChatMessage.value.trim()
        }
      })

      // Replace the user's input with the enhanced prompt
      userChatMessage.value = response.enhanced_content
      // Adjust textarea height for the new content
      nextTick(() => {
        adjustTextareaHeight()
      })
    } catch (error) {
      console.error('Failed to enhance prompt:', error)
      enhancementError.value = 'Failed to enhance prompt. Please try again.'

      // Auto-hide error message after 5 seconds
      setTimeout(() => {
        enhancementError.value = null
      }, 5000)
    } finally {
      isEnhancingPrompt.value = false
    }
  }

  const isPromptPopoverOpen = inject<Ref<boolean>>('isPromptPopoverOpen', ref(false))

  const openPromptTemplates = () => {
    showAtomPopover.value = false
    isPromptPopoverOpen.value = true
  }

  // ---------------
  // Atom popover multi-panel
  // ---------------

  const showPlusPopover = ref(false)
  const plusPanel = ref<'main' | 'documents'>('main')
  const showVerdictFeatureHint = ref(false)
  const isDismissingVerdictFeatureHint = ref(false)

  watch(showPlusPopover, (open) => {
    if (!open && showVerdictFeatureHint.value && !isDismissingVerdictFeatureHint.value) {
      nextTick(() => {
        showPlusPopover.value = true
      })
      return
    }

    if (!open) plusPanel.value = 'main'
  })

  const showAtomPopover = ref(false)
  const atomPanel = ref<'main' | 'documents' | 'snippets' | 'participants'>('main')

  watch(showAtomPopover, (open) => {
    if (!open) {
      atomPanel.value = 'main'
    } else {
      loadSnippets()
    }
  })

  // ---------------
  // Documents panel
  // ---------------

  // Get documents based on context
  const documents = computed(() => {
    if (sessionType === AIChatSessionTypeEnum.CASE) {
      return documentProcessor.value?.documents || []
    } else if (sessionType === AIChatSessionTypeEnum.GLOBAL && sessionGroupData.value?.documents) {
      return sessionGroupData.value.documents
    }
    return []
  })

  // Get the appropriate document processor
  const documentProcessor = computed(() => {
    if (sessionType === AIChatSessionTypeEnum.CASE && caseData.value?.uuid) {
      return caseStore.documentProcessor
    } else if (sessionType === AIChatSessionTypeEnum.GLOBAL) {
      if (activeChatSession.value?.uuid) {
        return globalChatStore.getSessionProcessor(activeChatSession.value.uuid)
      }
      return null
    }
    return null
  })

  // ---------------
  // Snippets panel
  // ---------------

  const snippets = ref<any[]>([])
  const snippetsLoading = ref(false)
  const editingSnippet = ref<any | null>(null)

  const loadSnippets = async () => {
    snippetsLoading.value = true
    try {
      const response = await SavedResponsesService.savedResponsesList({ sessionType })
      snippets.value = (response.results || []).sort(
        (a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
    } catch (error) {
      console.error('Error loading snippets:', error)
      snippets.value = []
    } finally {
      snippetsLoading.value = false
    }
  }

  // Count badges for atom main panel
  const participantsCount = computed(() => caseData.value?.data?.entities?.length ?? 0)

  const openSnippetEditor = (snippet: any) => {
    editingSnippet.value = snippet
    showAtomPopover.value = false
  }

  const closeSnippetEditor = () => {
    editingSnippet.value = null
  }

  const handleSnippetUpdated = () => {
    closeSnippetEditor()
    loadSnippets()
  }

  // Provide session type to child components
  provide('sessionType', sessionType)

  // ---------------
  // Court database toggle
  // ---------------

  const courtDatabaseFlagEnabled = inject<Ref<boolean>>('courtDatabaseFlagEnabled', ref(false))

  // Inject the reactive ref provided by AiChat so it can be read when sending messages
  const courtDbEnabled = inject<Ref<boolean>>('courtDbEnabled', ref(false))
  const setCourtDbEnabled = inject<(value: boolean) => Promise<void>>(
    'setCourtDbEnabled',
    async (value: boolean) => {
      courtDbEnabled.value = value
    }
  )

  const toggleCourtDb = () => {
    setCourtDbEnabled(!courtDbEnabled.value)
  }

  const dismissVerdictFeatureHint = async () => {
    isDismissingVerdictFeatureHint.value = true
    showVerdictFeatureHint.value = false
    showPlusPopover.value = false
    nextTick(() => {
      isDismissingVerdictFeatureHint.value = false
    })

    if (!userStore.user || (userStore.user as any)?.ai_chat_verdict_hint_seen) {
      return
    }

    ;(userStore.user as any).ai_chat_verdict_hint_seen = true

    try {
      await UsersService.usersPartialUpdate({
        uuid: userStore.user.uuid,
        requestBody: {
          ai_chat_verdict_hint_seen: true
        } as any
      })
    } catch (error) {
      console.error('Failed to mark AI chat verdict hint as seen:', error)
    }
  }

  watch(
    courtDatabaseFlagEnabled,
    (isEnabled) => {
      if (!isEnabled) return
      if (!userStore.user || (userStore.user as any)?.ai_chat_verdict_hint_seen) return
      showVerdictFeatureHint.value = true
      showPlusPopover.value = true
      plusPanel.value = 'main'
    },
    { immediate: true }
  )

  const handlePlusPopoverAutoFocus = (event: Event) => {
    if (showVerdictFeatureHint.value) {
      event.preventDefault()
      nextTick(() => {
        courtDatabaseToggleRef.value?.focus()
      })
    }
  }

  const closePromptPopover = () => {
    isPromptPopoverOpen.value = false
  }

  // Handle prefill from template
  const prefillFromTemplate = (content: string) => {
    userChatMessage.value = content
    nextTick(() => {
      adjustTextareaHeight()
    })
  }

  const handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  // Computed property to determine if status should adjust input spacing
  const hasDocumentStatus = computed(() => {
    return documentStatusRef.value?.hasActivity || documentStatusRef.value?.showSuccessMessage
  })

  // Auto-resize textarea functionality
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.value
    if (!textarea) return

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto'

    // Calculate line height (approximately 1.25rem with leading-5)
    const lineHeight = 20 // 1.25rem = 20px
    const minRows = 3
    const maxRows = 9
    const minHeight = lineHeight * minRows
    const maxHeight = lineHeight * maxRows

    // Get the scroll height and constrain it
    const scrollHeight = textarea.scrollHeight
    const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight)

    // Set the new height
    textarea.style.height = `${newHeight}px`

    // Enable/disable scrolling based on whether we've reached max height
    if (scrollHeight > maxHeight) {
      textarea.style.overflowY = 'auto'
    } else {
      textarea.style.overflowY = 'hidden'
    }
  }

  // Watch for changes in userChatMessage to adjust height
  watch(userChatMessage, () => {
    nextTick(() => {
      adjustTextareaHeight()
    })
  })

  // Initialize textarea height on mount
  onMounted(() => {
    nextTick(() => {
      adjustTextareaHeight()
    })
  })
</script>

<template>
  <!-- Message Input -->
  <div
    class="mt-auto bg-gray-50 px-4 pb-4"
    data-cy="message-input-container"
  >
    <div class="mx-auto max-w-4xl">
      <!-- Document Processing Status -->
      <DocumentProcessingStatus
        ref="documentStatusRef"
        :documents="documentProcessingData.documents"
        :processing-documents="documentProcessingData.processingDocuments"
        :active-uploads="documentProcessingData.activeUploads"
        :documents-with-errors="documentProcessingData.documentsWithErrors"
        :auto-hide-success-after="3000"
        :show-only-when-active="false"
        :compact="false"
      />

      <!-- Enhancement Error Message -->
      <Transition name="fade">
        <div
          v-if="enhancementError"
          class="mb-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        >
          <div class="flex items-center gap-2">
            <Info class="size-4 shrink-0" />
            <span>{{ enhancementError }}</span>
          </div>
        </div>
      </Transition>

      <div
        class="relative"
        :class="hasDocumentStatus ? 'mt-2' : 'mt-4'"
      >
        <div
          v-if="showVerdictFeatureHint"
          class="pointer-events-auto absolute bottom-full left-4 z-20 mb-3"
        >
          <AiChatVerdictFeatureHint @dismiss="dismissVerdictFeatureHint" />
        </div>

        <div
          class="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
          data-cy="message-input-wrapper"
        >
          <textarea
            ref="textareaRef"
            v-model="userChatMessage"
            :disabled="!!isActiveSessionGenerating"
            class="block w-full resize-none overflow-y-auto border-0 pb-2 pe-4 ps-2 pt-2 leading-5 focus:outline-none
              focus:ring-0 disabled:pointer-events-none disabled:opacity-50 md:text-sm
              [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300
              [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar]:w-2"
            :placeholder="t('YourMessage')"
            rows="3"
            data-cy="chat-message-input"
            @keydown="handleKeydown"
            @input="adjustTextareaHeight"
          ></textarea>

          <div
            class="mx-2 my-2 flex items-center justify-between gap-x-1"
            data-cy="message-input-actions"
          >
            <!-- Left: + button, atom button, court DB chip -->
            <div
              class="flex items-center gap-x-1.5"
              data-cy="left-button-group"
            >
              <div class="relative">
                <!-- "+" Popover: Upload documents + Urteilsdatenbank toggle -->
                <Popover v-model:open="showPlusPopover">
                  <PopoverTrigger as-child>
                    <button
                      type="button"
                      :disabled="!!isActiveSessionGenerating"
                      class="flex size-8 items-center justify-center rounded-md text-gray-600 transition-colors hover:bg-gray-100
                        focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                      :class="{ 'bg-gray-100': showPlusPopover }"
                      data-cy="plus-menu-button"
                    >
                      <Plus class="size-5 shrink-0" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    side="top"
                    class="overflow-hidden p-0"
                    :class="plusPanel === 'main' ? 'w-52' : 'w-[350px]'"
                    @open-auto-focus="handlePlusPopoverAutoFocus"
                  >
                    <!-- ── MAIN PANEL ── -->
                    <div
                      v-if="plusPanel === 'main'"
                      class="p-1.5"
                    >
                      <!-- Upload documents -->
                      <button
                        v-if="
                          (sessionType === AIChatSessionTypeEnum.CASE && documentProcessor) ||
                          sessionType === AIChatSessionTypeEnum.GLOBAL
                        "
                        type="button"
                        class="flex h-8 w-full items-center overflow-hidden rounded-md px-1.5 text-sm font-medium text-gray-800
                          hover:bg-gray-100"
                        data-cy="upload-documents-button"
                        @click="plusPanel = 'documents'"
                      >
                        <Upload
                          class="size-5 shrink-0 text-gray-500"
                          :stroke-width="1.5"
                        />
                        <span class="flex-1 px-1.5 text-left">{{ t('UploadDocuments') }}</span>
                      </button>
                      <!-- Urteilsdatenbank toggle -->
                      <template v-if="courtDatabaseFlagEnabled">
                        <button
                          ref="courtDatabaseToggleRef"
                          type="button"
                          class="flex h-8 w-full items-center overflow-hidden rounded-md text-sm font-medium text-gray-800"
                          :class="[
                            showVerdictFeatureHint
                              ? 'border-[3px] border-purple-600 px-[3px]'
                              : 'px-1.5 hover:bg-gray-100'
                          ]"
                          data-cy="court-database-toggle"
                          @click="toggleCourtDb()"
                        >
                          <Scale
                            class="size-5 shrink-0 text-gray-500"
                            :stroke-width="1.5"
                          />
                          <span class="flex-1 px-1.5 text-left">{{ t('CourtDatabase') }}</span>
                          <div
                            class="relative ml-2 h-4 w-7 rounded-full transition-colors"
                            :class="courtDbEnabled ? 'bg-purple-600' : 'bg-gray-300'"
                          >
                            <div
                              class="absolute top-0.5 h-3 w-3 rounded-full bg-white shadow-sm transition-transform"
                              :class="courtDbEnabled ? 'left-3.5' : 'left-0.5'"
                            />
                          </div>
                        </button>
                      </template>
                    </div>

                    <!-- ── DOCUMENTS PANEL ── -->
                    <AiChatDocumentsPanel
                      v-else-if="plusPanel === 'documents'"
                      :title="t('UploadDocuments')"
                      :show-documents-list="false"
                      :document-processor="documentProcessor"
                      @back="plusPanel = 'main'"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <!-- Atom button: multi-panel popover -->
              <Popover v-model:open="showAtomPopover">
                <PopoverTrigger as-child>
                  <button
                    type="button"
                    :disabled="!!isActiveSessionGenerating"
                    class="flex size-8 items-center justify-center rounded-md text-gray-600 transition-colors hover:bg-gray-100
                      focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                    :class="{ 'bg-gray-100': showAtomPopover }"
                    data-cy="atom-menu-button"
                  >
                    <Atom class="size-5 shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  side="top"
                  class="overflow-hidden p-0"
                  :class="atomPanel === 'main' ? 'w-[280px]' : 'w-[350px]'"
                >
                  <!-- ── MAIN PANEL ── -->
                  <div
                    v-if="atomPanel === 'main'"
                    class="p-1.5"
                  >
                    <!-- Promptvorlagen -->
                    <button
                      type="button"
                      class="flex h-8 w-full items-center overflow-hidden rounded-md px-1.5 text-sm font-medium text-gray-800
                        hover:bg-gray-100"
                      data-cy="prompt-templates-button"
                      @click="openPromptTemplates"
                    >
                      <ListRestart
                        class="size-5 shrink-0 text-gray-500"
                        :stroke-width="1.5"
                      />
                      <span class="flex-1 px-1.5 text-left">{{ t('PromptTemplates') }}</span>
                    </button>
                    <!-- Snippets -->
                    <button
                      type="button"
                      class="flex h-8 w-full items-center overflow-hidden rounded-md px-1.5 text-sm font-medium text-gray-800
                        hover:bg-gray-100"
                      data-cy="snippets-button"
                      @click="atomPanel = 'snippets'"
                    >
                      <SnippetIcon class="size-5 shrink-0 text-gray-500" />
                      <span class="flex-1 px-1.5 text-left">{{ t('Snippets') }}</span>
                      <span
                        v-if="snippets.length"
                        class="mr-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[10px]
                          font-semibold text-gray-800"
                      >
                        {{ snippets.length }}
                      </span>
                    </button>
                    <!-- Chat-Dokumente -->
                    <button
                      v-if="
                        (sessionType === AIChatSessionTypeEnum.CASE && documentProcessor) ||
                        sessionType === AIChatSessionTypeEnum.GLOBAL
                      "
                      type="button"
                      class="flex h-8 w-full items-center overflow-hidden rounded-md px-1.5 text-sm font-medium text-gray-800
                        hover:bg-gray-100"
                      data-cy="chat-documents-button"
                      @click="atomPanel = 'documents'"
                    >
                      <FileText
                        class="size-5 shrink-0 text-gray-500"
                        :stroke-width="1.5"
                      />
                      <span class="flex-1 px-1.5 text-left">{{ t('ChatDocuments') }}</span>
                      <span
                        v-if="documents.length"
                        class="mr-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[10px]
                          font-semibold text-gray-800"
                      >
                        {{ documents.length }}
                      </span>
                    </button>
                    <!-- Beteiligte (case only) -->
                    <button
                      v-if="sessionType === AIChatSessionTypeEnum.CASE"
                      type="button"
                      class="flex h-8 w-full items-center overflow-hidden rounded-md px-1.5 text-sm font-medium text-gray-800
                        hover:bg-gray-100"
                      @click="atomPanel = 'participants'"
                    >
                      <Users
                        class="size-5 shrink-0 text-gray-500"
                        :stroke-width="1.5"
                      />
                      <span class="flex-1 px-1.5 text-left">{{ t('Participants') }}</span>
                      <span
                        v-if="participantsCount"
                        class="mr-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[10px]
                          font-semibold text-gray-800"
                      >
                        {{ participantsCount }}
                      </span>
                    </button>
                  </div>

                  <!-- ── DOCUMENTS PANEL ── -->
                  <AiChatDocumentsPanel
                    v-else-if="atomPanel === 'documents'"
                    :title="t('ChatDocuments')"
                    :count="documents.length || undefined"
                    :document-processor="documentProcessor"
                    @back="atomPanel = 'main'"
                  />

                  <!-- ── PARTICIPANTS PANEL ── -->
                  <AiChatParticipantsPanel
                    v-else-if="atomPanel === 'participants'"
                    @back="atomPanel = 'main'"
                    @close="showAtomPopover = false"
                  />

                  <!-- ── SNIPPETS PANEL ── -->
                  <AiChatSnippetsPanel
                    v-else-if="atomPanel === 'snippets'"
                    :snippets="snippets"
                    :snippets-loading="snippetsLoading"
                    @back="atomPanel = 'main'"
                    @close="showAtomPopover = false"
                    @edit="openSnippetEditor"
                    @refresh="loadSnippets"
                  />
                </PopoverContent>
              </Popover>

              <!-- Urteilsdatenbank chip (shown when active) -->
              <button
                v-if="courtDatabaseFlagEnabled && courtDbEnabled"
                type="button"
                class="flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-sm
                  font-medium text-gray-600 transition-colors hover:bg-gray-50"
                data-cy="court-database-chip"
                @click="toggleCourtDb"
              >
                <Scale class="size-3.5 shrink-0" />
                {{ t('CourtDatabase') }}
                <X
                  class="size-3.5 shrink-0"
                  @click.stop="toggleCourtDb"
                />
              </button>
            </div>

            <!-- Right Button Group -->
            <div
              class="flex items-center gap-x-1"
              data-cy="right-button-group"
            >
              <!-- Enhance Prompt Button -->
              <div class="relative">
                <button
                  type="button"
                  :disabled="
                    isEnhancingPrompt || isActiveSessionGenerating || !userChatMessage.trim()
                  "
                  class="inline-flex size-8 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white
                    text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:bg-gray-50
                    focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                  data-cy="enhance-prompt-button"
                  @click="enhancePrompt"
                  @mouseenter="showEnhanceTooltip = true"
                  @mouseleave="showEnhanceTooltip = false"
                >
                  <span
                    v-if="isEnhancingPrompt"
                    class="animate-spin"
                  >
                    <Loader2 class="size-4" />
                  </span>
                  <span v-else>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 18 18"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.25"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="size-4 shrink-0"
                    >
                      <path
                        d="M11.5833 3.25041L14.9167 6.58374M3.25 4.08374V7.41707M14.9167 10.7504V14.0837M7.41667 0.750407V2.41707M4.91667 5.75041H1.58333M16.5833 12.4171H13.25M8.25 1.58374H6.58333M16.7283 4.76032C17.1689 4.31984 17.4164 3.72237 17.4165 3.09936C17.4166 2.47635 17.1692 1.87883 16.7287 1.43823C16.2882 0.997643 15.6908 0.750078 15.0677 0.75C14.4447 0.749922 13.8472 0.997337 13.4066 1.43782L2.28495 12.562C2.09146 12.7549 1.94837 12.9924 1.86828 13.2537L0.767445 16.8803C0.745908 16.9524 0.744282 17.0289 0.762738 17.1019C0.781194 17.1748 0.819046 17.2413 0.872276 17.2945C0.925506 17.3476 0.992129 17.3854 1.06508 17.4037C1.13803 17.4221 1.21458 17.4203 1.28661 17.3987L4.91411 16.2987C5.17509 16.2193 5.41259 16.0771 5.60578 15.8845L16.7283 4.76032Z"
                      />
                    </svg>
                  </span>
                  <span class="sr-only">
                    {{ isEnhancingPrompt ? t('Enhancing') : t('EnhancePrompt') }}
                  </span>
                </button>
                <!-- Custom Tooltip -->
                <Transition name="tooltip">
                  <div
                    v-if="showEnhanceTooltip && !isEnhancingPrompt"
                    class="absolute bottom-full left-1/2 mb-2 -translate-x-1/2 transform whitespace-nowrap rounded bg-gray-900
                      px-2 py-1 text-xs text-white shadow-lg"
                  >
                    {{ t('EnhancePrompt') }}
                    <div
                      class="absolute left-1/2 top-full -translate-x-1/2 transform border-4 border-transparent border-t-gray-900"
                    ></div>
                  </div>
                </Transition>
              </div>

              <!-- Send Button -->
              <button
                type="button"
                :disabled="!!isActiveSessionGenerating || !userChatMessage.trim()"
                class="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-purple-600 text-sm font-medium
                  text-white hover:bg-purple-700 focus:bg-purple-700 focus:outline-none disabled:pointer-events-none
                  disabled:opacity-50"
                data-cy="send-message-button"
                @click="sendMessage"
              >
                <span
                  v-if="isActiveSessionGenerating"
                  class="animate-spin"
                >
                  <Loader2 class="size-4" />
                </span>
                <span v-else>
                  <SendHorizontal class="size-5 shrink-0" />
                </span>
                <span class="sr-only">
                  {{ isActiveSessionGenerating ? t('Generating') : t('SendMessage') }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- End Message Input -->

  <!-- Prompt Templates Dialog (opened from atom popover) -->
  <Dialog v-model:open="isPromptPopoverOpen">
    <DialogContent class="max-w-5xl overflow-hidden rounded-lg p-0">
      <PromptTemplatesDialog
        @close="closePromptPopover"
        @prefill="prefillFromTemplate"
      />
    </DialogContent>
  </Dialog>

  <EditSavedResponseDialog
    v-if="editingSnippet"
    :snippet="editingSnippet"
    @close="closeSnippetEditor"
    @updated="handleSnippetUpdated"
  />
</template>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition:
      opacity 0.4s ease,
      transform 0.3s ease;
  }

  .fade-enter-from {
    opacity: 0;
    transform: translateY(-5px);
  }

  .fade-leave-to {
    opacity: 0;
    transform: translateY(5px);
  }

  .fade-enter-to,
  .fade-leave-from {
    opacity: 1;
    transform: translateY(0);
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
</style>
